// This widget registry refers to the components that are available for selection in the Insight Builder.
// This is a list of all the widgets from an application-centric view (what counts as a widget when looking at the app not printing).
// How they will be converted to images to be presented in the insight download prewview is based on what portions have the INSIGHT_WIDGET_PRINTABLE_CLASS + an id

// i.e. Car Traffic widget for insight building is 1 'component' but has 3 'widgets' that will be separate in the insight download for printing
// APP:Car Traffic widget -> PRINTING: Car Traffic Map, AADT Hour, AADT Day
// But more commonly like Day of Week, Hour of Day, Length of Stay, etc. are a single app 'component' and printable 'widget'

import { InsightBuilderWidget } from 'src/types/insight-builder'
// Property records
import { PropertyRecordsParcelWidget } from 'src/components/insights/property-records/parcel/definition'
import { BuildingLotAndLocationWidget } from 'src/components/insights/property-records/building-lot-and-location/definition'
import { PossibleOwnersWidget } from 'src/components/insights/property-records/possible-owners/definition'
import { PropertyDebtsWidget } from 'src/components/insights/property-records/property-debts/definition'
import { PropertySalesWidget } from 'src/components/insights/property-records/property-sales/definition'
import { PropertyTaxesWidget } from 'src/components/insights/property-records/property-taxes/definition'
// Zoning
import { ZoningCodesWidget } from 'src/components/insights/zoning/codes/definition'
import { ZoningDetailsWidget } from 'src/components/insights/zoning/details/definition'
import {
  ZoningAsOfRightsWidget,
  ZoningConditionalUsesWidget,
  ZoningProhibitedUseCasesWidget,
} from 'src/components/insights/zoning/uses/definition'
// Location visits
import { CrossVisitionWidget } from 'src/components/insights/location-visits/cross-visitation/definition'
import { DayOfWeekWidget } from 'src/components/insights/location-visits/day-of-week/definition'
import { HourOfDayWidget } from 'src/components/insights/location-visits/hour-of-day/definition'
import { LengthOfStayWidget } from 'src/components/insights/location-visits/length-of-stay/definition'
import { VarianceOfVisitsWidget } from 'src/components/insights/location-visits/variance-of-visits/definition'
import { VisitsTrendsWidget } from 'src/components/insights/location-visits/visits-trends/definition'
// True trade area
import { OverviewWidget } from 'src/components/insights/true-trade-area/overview/definition'
import { VisitorOriginWidget } from 'src/components/insights/true-trade-area/visitor-origin/definition'
// Area visits
import { AreaVisitsWidget } from 'src/components/insights/area-visits/area-visits/definition'
import { AreaVisitsDailyFootTrafficWidget } from 'src/components/insights/area-visits/day-of-week/defintion'
import { AreaVisitsHourlyFootTrafficWidget } from 'src/components/insights/area-visits/hour-of-day/definition'
import { NearbyLocationsWidget } from 'src/components/insights/area-visits/nearby-locations/definition'
import { AreaVisitsTrendsWidget } from 'src/components/insights/area-visits/visit-trends/definition'
// Rank
import { BrandRankListWidget, CategoryRankListWidget } from 'src/components/insights/rank/rank-list/definition'
import {
  CategoryPercentileRankingWidget,
  BrandPercentileRankingWidget,
} from 'src/components/insights/rank/percentile-ranking/definition'
import {
  BrandRankOverTimeWidget,
  CategoryRankOverTimeWidget,
} from 'src/components/insights/rank/rank-over-time/definition'
// Demographics
import { DemographicsPopulationWidget } from 'src/components/insights/demographics/cards/population/definition'
import { DemographicsAgeWidget } from 'src/components/insights/demographics/cards/age/definition'
import { DemographicsRaceEthnicityWidget } from 'src/components/insights/demographics/cards/race-ethnicity/definition'
import { DemographicsHouseholdIncomeWidget } from 'src/components/insights/demographics/cards/income/definition'
import { DemographicsHouseholdsWidget } from 'src/components/insights/demographics/cards/households/definition'
import { DemographicsHousingUnitsWidget } from 'src/components/insights/demographics/cards/housing-units/definition'
import { DemographicsGenderWidget } from 'src/components/insights/demographics/cards/gender/definition'
import { DemographicsValueOwnerOccupiedHousingUnitsWidget } from 'src/components/insights/demographics/cards/owner-housing-units/definition'
import { DemographicsTypesHousingUnitsWidget } from 'src/components/insights/demographics/cards/types-housing-units/definition'
import { DemographicsEducationWidget } from 'src/components/insights/demographics/cards/education/definition'
import { DemographicsLaborForceWidget } from 'src/components/insights/demographics/cards/labor-force/definition'
import { DemographicsTransportationWidget } from 'src/components/insights/demographics/cards/transportation/definition'
import { DemographicsTravelTimeWidget } from 'src/components/insights/demographics/cards/travel-time/definition'
import { DemographicsPopulationByOccupationWidget } from 'src/components/insights/demographics/cards/population-occupation/definition'
import { DemographicsPopulationByIndustryWidget } from 'src/components/insights/demographics/cards/population-industry/definition'
import { DemographicsConsumerSpendWidget } from 'src/components/insights/demographics/cards/consumer-spend/definition'
import { DemographicsMaritalStatusWidget } from 'src/components/insights/demographics/cards/marital-status/definition'
// Car Traffic (map + aadt hour + aadt day as a grouped widget)
import { CarTrafficWidget } from 'src/components/insights/car-traffic/definition'
// Radius Rings Trade Area
import {
  RadiusRingsTradeAreaMapWidget,
  RadiusRingsTradeAreaOverviewWidget,
  RadiusRingsTradeAreaConsumerSpendWidget,
  RadiusRingsTradeAreaDemographicsWidget,
} from 'src/components/insights/radius-rings-trade-area/definition'

// Drive Time Trade Area
import {
  DriveTimeTradeAreaMapWidget,
  DriveTimeTradeAreaOverviewWidget,
  DriveTimeTradeAreaConsumerSpendWidget,
  DriveTimeTradeAreaDemographicsWidget,
} from 'src/components/insights/drive-time-trade-area/definition'

// Misc
import { InsightImageWidget } from 'src/components/insight-image/definition'
import { InsightTextWidget } from 'src/components/insight-text/definition'
import { InsightMapWidget } from 'src/components/insight-map/definition'
import { TenantFinderRecommendationsWidget } from 'src/components/insights/tenant-finder-recommendations/definition'

const PropertyRecordsWidgets: InsightBuilderWidget[] = [
  PropertyRecordsParcelWidget,
  BuildingLotAndLocationWidget,
  PossibleOwnersWidget,
  PropertyDebtsWidget,
  PropertySalesWidget,
  PropertyTaxesWidget,
]

const ZoningWidgets: InsightBuilderWidget[] = [
  ZoningCodesWidget,
  ZoningDetailsWidget,
  ZoningConditionalUsesWidget,
  ZoningAsOfRightsWidget,
  ZoningProhibitedUseCasesWidget,
]

export const ParcelWidgets: InsightBuilderWidget[] = [...PropertyRecordsWidgets, ...ZoningWidgets]

const LocationVisitsWidgets: InsightBuilderWidget[] = [
  CrossVisitionWidget,
  DayOfWeekWidget,
  HourOfDayWidget,
  LengthOfStayWidget,
  VarianceOfVisitsWidget,
  VisitsTrendsWidget,
]

const TrueTradeAreaWidgets: InsightBuilderWidget[] = [OverviewWidget, VisitorOriginWidget]

const AreaVisitsWidgets: InsightBuilderWidget[] = [
  AreaVisitsWidget,
  AreaVisitsDailyFootTrafficWidget,
  AreaVisitsHourlyFootTrafficWidget,
  NearbyLocationsWidget,
  AreaVisitsTrendsWidget,
]

const RankWidgets: InsightBuilderWidget[] = [BrandRankListWidget, BrandPercentileRankingWidget, BrandRankOverTimeWidget]
const CategoryRankWidgets: InsightBuilderWidget[] = [
  CategoryRankListWidget,
  CategoryPercentileRankingWidget,
  CategoryRankOverTimeWidget,
]

const ShoppingCenterOverviewWidgets: InsightBuilderWidget[] = [TenantFinderRecommendationsWidget]

export const MobilityWidgets: InsightBuilderWidget[] = [
  ...LocationVisitsWidgets,
  ...TrueTradeAreaWidgets,
  ...AreaVisitsWidgets,
  ...RankWidgets,
  ...CategoryRankWidgets,
  ...ShoppingCenterOverviewWidgets,
]

const DemographicsWidgets: InsightBuilderWidget[] = [
  DemographicsPopulationWidget,
  DemographicsAgeWidget,
  DemographicsRaceEthnicityWidget,
  DemographicsHouseholdIncomeWidget,
  DemographicsHouseholdsWidget,
  DemographicsHousingUnitsWidget,
  DemographicsGenderWidget,
  DemographicsValueOwnerOccupiedHousingUnitsWidget,
  DemographicsTypesHousingUnitsWidget,
  DemographicsEducationWidget,
  DemographicsLaborForceWidget,
  DemographicsTransportationWidget,
  DemographicsTravelTimeWidget,
  DemographicsPopulationByOccupationWidget,
  DemographicsPopulationByIndustryWidget,
  DemographicsConsumerSpendWidget,
  DemographicsMaritalStatusWidget,
]
const CarTrafficWidgets: InsightBuilderWidget[] = [CarTrafficWidget]

const RadiusRingsTradeAreaWidgets: InsightBuilderWidget[] = [
  RadiusRingsTradeAreaMapWidget,
  RadiusRingsTradeAreaOverviewWidget,
  RadiusRingsTradeAreaConsumerSpendWidget,
  RadiusRingsTradeAreaDemographicsWidget,
]

const DriveTimeTradeAreaWidgets: InsightBuilderWidget[] = [
  DriveTimeTradeAreaMapWidget,
  DriveTimeTradeAreaOverviewWidget,
  DriveTimeTradeAreaConsumerSpendWidget,
  DriveTimeTradeAreaDemographicsWidget,
]

export const AreaWidgets = [
  ...DemographicsWidgets,
  ...CarTrafficWidgets,
  ...RadiusRingsTradeAreaWidgets,
  ...DriveTimeTradeAreaWidgets,
]

export const MiscWidgets = [InsightTextWidget, InsightImageWidget, InsightMapWidget] as const

export const ALL_WIDGETS = [...ParcelWidgets, ...MobilityWidgets, ...AreaWidgets, ...MiscWidgets]
